import { Inject } from '@odinjs/odin';
import { ButtonAPI, FieldAPI, FormAPI, ToastNotificationAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { ContextType, Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnChange, OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { ChangeEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { HttpResponse } from '@philips-internal/oswald-ui-core/lib/services/http-service';
import { isMobile } from '@philips-internal/oswald-ui-utils';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { ToastResponse } from '../../interfaces/ToastResponse';
import { HttpServiceError, HttpServiceResponse, PatientHttpService } from '../../services/patient-http-service';
import { showToastNotificationMessage, showToastNotificationSuccess, showToastNotificationWarning } from '../../utils/toast-notification-utils';
import { CONFIRMATION_FIELD_VALID, confirmationFieldValidator, CRYPTOPW_VALIDATOR, CRYPTOPW_VALIDATOR_DEPRECATED, cryptoPWValidator, cryptoPWValidatorDeprecated, NEW_CRYPTOPW_VALIDATOR, newCryptoPWValidatorValidator } from '../../utils/user-util';
import BaseController from '../BaseController';

interface Message {
  messages: string[];
}

@Controller({ component: 'renew-layout', fragment: 'change-expired-password' })
class ChangeExpiredPasswordController extends BaseController {

  @Inject('old-password-field')
    fieldActualPassword!: FieldAPI;

  @Inject('new-password-field')
    fieldNewPassword!: FieldAPI;

  @Inject('confirm-password-field')
    fieldNewConfirmPassword!: FieldAPI;

  @Inject('renew-form')
    formPassword!: FormAPI;

  @Inject('change-password-submit-button')
    confirmBtn!: ButtonAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @OnMount({ target: 'renew-form' })
  onMountModalAltSenha(): void {
    this.fieldActualPassword.addLocalValidator(CRYPTOPW_VALIDATOR_DEPRECATED, cryptoPWValidatorDeprecated);
    this.fieldNewPassword.addLocalValidator(CRYPTOPW_VALIDATOR, cryptoPWValidator);
    this.fieldNewConfirmPassword.addLocalValidator(CONFIRMATION_FIELD_VALID, (value) => {
      const confirmationValue = this.formPassword.getRecord()['newPassword'] || '';
      return confirmationFieldValidator(value, confirmationValue);
    });
    this.fieldNewPassword.addLocalValidator(NEW_CRYPTOPW_VALIDATOR, (value) => {
      const senhaAtual = this.formPassword.getRecord()['password'] || '';
      return newCryptoPWValidatorValidator(value, senhaAtual);
    });
  }

  @OnMount({ target: 'toast-notification-desktop'})
  handleToastDesktopLoading(event: MountEvent): void {
    const toastDesktopAPI = event.target as ToastNotificationAPI;
    const message = this.context.get('message') as string;
    if(isMobile() && !!message) {
      toastDesktopAPI.setVisible(false);
      showToastNotificationWarning(message, undefined, true);
      return;
    }
    if(isMobile() && !message) {
      toastDesktopAPI.setVisible(false);
      return;
    }
    toastDesktopAPI.setHighlight(false);
    toastDesktopAPI.setMessage(message);
    toastDesktopAPI.onClose.listen(() => toastDesktopAPI.setVisible(false));
  }

  @OnChange({ target: ['renew-form'] })
  onChangeFormAltSenha({ value }: ChangeEvent<Record<string, Value>>): void {
    this.confirmBtn.setEnabled(this.isDataValid(value));
  }

  isDataValid(value: Record<string, Value>): boolean {
    return this.fieldNewPassword.isValid() &&
      this.fieldActualPassword.isValid() &&
      this.fieldNewConfirmPassword.isValid() &&
      Boolean(value['password']) &&
      Boolean(value['newPassword']) &&
      Boolean(value['confirmPassword']) &&
      (value['newPassword'] === value['confirmPassword']);
  }

  @OnClick({ target: ['change-password-submit-button'] })
  onClickAltSenha(): void {
    this.confirmBtn.setEnabled(false);
    this.formPassword.setDisabled(true);
    this.renewPassword();
  }

  renewPassword(): void {
    const body = this.formPassword.getRecord();
    body.login = this.context.get('username');
    PatientHttpService.post(APPLICATION_ENDPOINTS.POST_RENEW_PASSWORD, {}, { body }).then(resp => {
      this.handleSuccess(resp);
    }).catch(({ response }: HttpServiceError<ToastResponse>) => {
      this.handleFailure(response);
    });
  }

  handleSuccess(resp: HttpResponse<unknown>): void {
    const record = resp.body as Message;
    if(record && record.messages) {
      const message = MessageService.translate(record.messages[0]);
      showToastNotificationSuccess(message, undefined, undefined, 'patient.forget.password.request.success');
    }
    this.close(new SubmitEvent())
  }

  close(submittedEvent: SubmitEvent): void {
    const { record } = submittedEvent;
    this.submittableFragmentContext.submit(new SubmitEvent({ record }));
  }

  handleFailure(resp?: HttpServiceResponse<ToastResponse>): void {
    this.formPassword.setEditing(true);
    this.confirmBtn.setEnabled(this.isDataValid(this.formPassword.getRecord()));
    const cookieToastId = 'd6537482-d5a4-4c7e-954a-ea2fc090af93';
    ToastService.close(cookieToastId);

    if (resp) {
      showToastNotificationMessage(resp.body, true);
    }
  }
}

export default ChangeExpiredPasswordController;
